"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import { cn, platformProps } from "../../lib";
import { touchTargetClassName } from "../../lib/touch-target";
import type { CheckboxPrimitiveProps } from "./types";

/**
 * Checkbox - Web implementation (Radix)
 *
 * Unstyled primitive. The atom applies styling via className and indicatorClassName,
 * and supplies the indicator's content (the tick) as children.
 * When the browser reports a coarse primary pointer, its tap area grows to at least
 * 44×44px (`touchTargetClassName`).
 */
const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxPrimitiveProps
>(
  (
    {
      checked,
      onCheckedChange,
      disabled = false,
      className,
      indicatorClassName,
      children,
      testID,
      id,
      accessibilityLabel,
      ...rest
    },
    ref,
  ) => (
    <CheckboxPrimitive.Root
      ref={ref}
      {...platformProps({ testID, slot: "checkbox", id })}
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      disabled={disabled}
      aria-label={accessibilityLabel}
      className={cn(touchTargetClassName, className)}
      {...rest}
    >
      <CheckboxPrimitive.Indicator className={indicatorClassName}>
        {children}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
